// Performance Monitoring — the PERIODIC collation of auditor and report-approver performance that CT 17
// clauses 7.2.9–7.2.11 require and M&P 3.5 describes. Everything here is computed from data AuditPM already
// captures job by job (report submissions, returns to the auditor, WSHC returns, approvals, feedback
// requests); nothing is re-keyed. "Record this review" freezes the period's figures with the QM's conclusion
// — that saved record, not this screen, is the evidence an assessor asks for.
// Admin / QM only (route-gated + RLS).
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { isOtherService } from '../lib/service'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO, subMonths } from 'date-fns'
import { toast, failed } from '../lib/toast'

const PERIODS = [[3, 'Last 3 months'], [6, 'Last 6 months'], [12, 'Last 12 months']]
const RETURN_OFFICE = 'Audit package returned to auditor'
const RETURN_WSHC = 'Package returned by WSHC'
const fmtD = d => { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch (e) { return d || '—' } }
const days = (a, b) => (a && b) ? Math.round((new Date(b) - new Date(a)) / 86400000) : null
const avg = arr => arr.length ? (arr.reduce((s, n) => s + n, 0) / arr.length) : null
const show = (n, dp = 1) => n == null ? '—' : Number(n).toFixed(dp).replace(/\.0$/, '')

// PostgREST caps every read at 1000 rows — paginate anything that grows with history.
async function allRows(table, cols, filter) {
  const out = []
  for (let from = 0; ; from += 1000) {
    let q = supabase.from(table).select(cols).range(from, from + 999)
    if (filter) q = filter(q)
    const { data, error } = await q
    if (error) throw error
    out.push(...(data || []))
    if (!data || data.length < 1000) break
  }
  return out
}

export default function PerformanceMonitoring() {
  const { profile } = useAuth()
  const [months, setMonths] = useState(6)
  const [from, setFrom] = useState(format(subMonths(new Date(), 6), 'yyyy-MM-dd'))
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [saved, setSaved] = useState([])
  const [remarks, setRemarks] = useState('')
  const [actions, setActions] = useState('')
  const [busy, setBusy] = useState(false)

  const setPeriod = (m) => { setMonths(m); setFrom(format(subMonths(new Date(), m), 'yyyy-MM-dd')); setTo(format(new Date(), 'yyyy-MM-dd')) }

  useEffect(() => { loadSaved() }, [])            // eslint-disable-line
  useEffect(() => { compute() }, [from, to])      // eslint-disable-line

  async function loadSaved() {
    const { data } = await supabase.from('performance_reviews').select('*').order('period_end', { ascending: false }).limit(20)
    setSaved(data || [])
  }

  async function compute() {
    setLoading(true); setErr('')
    try {
      // 1) Jobs whose AUDIT falls in the period — the unit of an auditor's work.
      // CT 17 auditor performance covers bizSAFE / SHMS audits only — never client Internal Audit or OTE jobs.
      const jobs = (await allRows('jobs',
        'id, audit_type, quote_ref, audit_date, auditor_name, auditor2_name, report_submitted_at, office_review_by, office_review_at, wshc_outcome, status, feedback_request_sent_at, clients(name)',
        q => q.gte('audit_date', from).lte('audit_date', to).not('audit_date', 'is', null))).filter(j => !isOtherService(j.audit_type))
      const jobIds = jobs.map(j => j.id)

      // 2) Report versions per job (max version = number of submissions; >1 means it was revised).
      const snaps = jobIds.length ? await allRows('audit_report_snapshots', 'job_id, version',
        q => q.in('job_id', jobIds)) : []
      const maxVer = {}
      for (const s of snaps) maxVer[s.job_id] = Math.max(maxVer[s.job_id] || 0, s.version || 0)

      // 3) Returns, from the job audit trail (one entry per actual return event).
      const trail = jobIds.length ? await allRows('audit_trail', 'entity_id, action, created_at',
        q => q.in('action', [RETURN_OFFICE, RETURN_WSHC]).in('entity_id', jobIds)) : []
      const officeRet = {}, wshcRet = {}
      for (const t of trail) {
        const m = t.action === RETURN_OFFICE ? officeRet : wshcRet
        m[t.entity_id] = (m[t.entity_id] || 0) + 1
      }

      // ── Auditors: one row per person who conducted an audit in the period ──
      const A = {}
      const bump = (name, j) => {
        if (!name) return
        const a = A[name] || (A[name] = { name, audits: 0, submitted: 0, versions: [], officeReturns: 0, wshcReturns: 0, turnaround: [], jobs: [] })
        a.audits++
        a.jobs.push(j.clients?.name || j.quote_ref || j.id)
        if (j.report_submitted_at) { a.submitted++; const d = days(j.audit_date, j.report_submitted_at); if (d != null) a.turnaround.push(d) }
        if (maxVer[j.id]) a.versions.push(maxVer[j.id])
        a.officeReturns += officeRet[j.id] || 0
        a.wshcReturns += wshcRet[j.id] || 0
      }
      for (const j of jobs) { bump(j.auditor_name, j); bump(j.auditor2_name, j) }

      // ── Report approvers: one row per person who approved a report in the period ──
      const P = {}
      for (const j of jobs) {
        if (!j.office_review_by || !j.office_review_at) continue
        const p = P[j.office_review_by] || (P[j.office_review_by] = { name: j.office_review_by, approved: 0, wshcReturns: 0, revisedBefore: 0 })
        p.approved++
        p.wshcReturns += wshcRet[j.id] || 0
        if ((maxVer[j.id] || 1) > 1) p.revisedBefore++
      }

      setRows({
        period: { from, to },
        totals: {
          audits: jobs.length,
          submitted: jobs.filter(j => j.report_submitted_at).length,
          feedbackRequested: jobs.filter(j => j.feedback_request_sent_at).length,
          wshcReturns: Object.values(wshcRet).reduce((s, n) => s + n, 0),
          officeReturns: Object.values(officeRet).reduce((s, n) => s + n, 0),
        },
        auditors: Object.values(A).sort((a, b) => b.audits - a.audits),
        approvers: Object.values(P).sort((a, b) => b.approved - a.approved),
      })
    } catch (e) {
      setErr(String(e.message || e) + '  (has sql/PERFORMANCE_MONITORING.sql been run?)')
    }
    setLoading(false)
  }

  async function record() {
    if (!rows) return
    if (!remarks.trim()) { toast('Write the review conclusion first', 'warning', { detail: 'This is the part the assessor reads' }); return }
    setBusy(true)
    const { error } = await supabase.from('performance_reviews').insert({
      period_start: from, period_end: to,
      data: { totals: rows.totals, auditors: rows.auditors, approvers: rows.approvers },
      remarks: remarks.trim(), actions: actions.trim() || null,
      reviewed_by: profile?.name || null, reviewed_on: new Date().toISOString().slice(0, 10),
    })
    setBusy(false)
    if (error) { failed("Couldn't record the review", error.message); return }
    setRemarks(''); setActions(''); loadSaved()
    toast('Review recorded', 'success')
  }

  const T = rows?.totals

  return (
    <div>
      <div className="page-header"><h1 style={{ margin: 0 }}>Performance Monitoring</h1></div>

      <div className="card" style={{ marginBottom: 12, fontSize: 13, color: 'var(--text2)' }}>
        The periodic review of auditor and report-approver performance (CT 17 7.2.9–7.2.11; M&amp;P 3.5).
        Figures are collated from the audits themselves — reports submitted, revisions, returns from the office
        and from WSHC, turnaround, and client feedback requested. Review the period, write the conclusion, and
        record it: the recorded review is the evidence that monitoring took place.
      </div>

      <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {PERIODS.map(([m, label]) => (
          <button key={m} className={`sm ${months === m ? 'primary' : ''}`} onClick={() => setPeriod(m)}>{label}</button>
        ))}
        <div className="field" style={{ margin: 0 }}><label>From</label><input type="date" value={from} onChange={e => { setMonths(0); setFrom(e.target.value) }} /></div>
        <div className="field" style={{ margin: 0 }}><label>To</label><input type="date" value={to} onChange={e => { setMonths(0); setTo(e.target.value) }} /></div>
      </div>

      {err && <div className="card" style={{ color: 'var(--danger)', marginBottom: 12 }}>{err}</div>}
      {loading ? <div className="card" style={{ color: 'var(--text3)' }}>Loading…</div> : !rows ? null : (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="stat-grid">
              <div className="stat-card"><div className="stat-val">{T.audits}</div><div className="stat-lbl">Audits in period</div></div>
              <div className="stat-card"><div className="stat-val">{T.submitted}</div><div className="stat-lbl">Reports submitted</div></div>
              <div className="stat-card"><div className="stat-val">{T.officeReturns}</div><div className="stat-lbl">Returned by office</div></div>
              <div className="stat-card"><div className="stat-val">{T.wshcReturns}</div><div className="stat-lbl">Returned by WSHC</div></div>
              <div className="stat-card"><div className="stat-val">{T.feedbackRequested}</div><div className="stat-lbl">Feedback requested</div></div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <h2 className="card-title">Auditors ({rows.auditors.length})</h2>
            {rows.auditors.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No audits in this period.</div> : (
              <div className="table-wrap"><table>
                <thead><tr>
                  <th>Auditor</th><th>Audits</th><th>Reports submitted</th><th>Avg versions</th>
                  <th>Returned by office</th><th>Returned by WSHC</th><th>Avg days audit → report</th>
                </tr></thead>
                <tbody>
                  {rows.auditors.map(a => (
                    <tr key={a.name}>
                      <td style={{ fontWeight: 500 }} title={a.jobs.join(', ')}>{a.name}</td>
                      <td>{a.audits}</td>
                      <td>{a.submitted}</td>
                      <td>{show(avg(a.versions), 2)}</td>
                      <td style={{ color: a.officeReturns ? 'var(--warn, #854F0B)' : 'inherit' }}>{a.officeReturns}</td>
                      <td style={{ color: a.wshcReturns ? 'var(--danger)' : 'inherit', fontWeight: a.wshcReturns ? 600 : 400 }}>{a.wshcReturns}</td>
                      <td>{show(avg(a.turnaround), 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>
              Avg versions above 1.0 means reports needed revision. A WSHC return is the strongest quality signal — treat any as a training-need trigger.
            </div>
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <h2 className="card-title">Report approvers ({rows.approvers.length})</h2>
            {rows.approvers.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No approvals in this period.</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Approver</th><th>Reports approved</th><th>Of which revised before approval</th><th>Returned by WSHC after approval</th></tr></thead>
                <tbody>
                  {rows.approvers.map(p => (
                    <tr key={p.name}>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td>{p.approved}</td>
                      <td>{p.revisedBefore}</td>
                      <td style={{ color: p.wshcReturns ? 'var(--danger)' : 'inherit', fontWeight: p.wshcReturns ? 600 : 400 }}>{p.wshcReturns}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>
              A report approved here and later returned by WSHC is the review measure for the approver, and is sampled again at internal audit (M&amp;P 6.1).
            </div>
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <h2 className="card-title">Record this review</h2>
            <div className="field"><label>Conclusion — competence and performance over the period *</label>
              <textarea rows={3} value={remarks} onChange={e => setRemarks(e.target.value)}
                placeholder="e.g. All auditors performed satisfactorily over the period. No WSHC returns attributable to auditor error. Client feedback raised no concerns." /></div>
            <div className="field"><label>Actions arising (training needs, follow-up)</label>
              <textarea rows={2} value={actions} onChange={e => setActions(e.target.value)}
                placeholder="e.g. Refresher on the revised checklist for all auditors in Q4 — added to the Training Programme." /></div>
            <button className="primary" disabled={busy} onClick={record}>{busy ? 'Recording…' : `Record review for ${fmtD(from)} – ${fmtD(to)}`}</button>
          </div>

          <div className="card">
            <h2 className="card-title">Recorded reviews ({saved.length})</h2>
            {saved.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>None recorded yet.</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>Period</th><th>Audits</th><th>WSHC returns</th><th>Conclusion</th><th>Reviewed by</th><th>On</th></tr></thead>
                <tbody>
                  {saved.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: 13 }}>{fmtD(r.period_start)} – {fmtD(r.period_end)}</td>
                      <td style={{ fontSize: 13 }}>{r.data?.totals?.audits ?? '—'}</td>
                      <td style={{ fontSize: 13 }}>{r.data?.totals?.wshcReturns ?? '—'}</td>
                      <td style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{r.remarks}{r.actions ? `\n\nActions: ${r.actions}` : ''}</td>
                      <td style={{ fontSize: 13 }}>{r.reviewed_by || '—'}</td>
                      <td style={{ fontSize: 13 }}>{fmtD(r.reviewed_on)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
